import FilterInput from '@Components/FilterInput/FilterInput';
import FilterResult from '@Components/FilterResult/FilterResult';
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AssetStatusServices from '@Services/AssetStatus.services';
import AssetTypeServices from '@Services/AssetType.services';
import LocationServices from '@Services/Location.services';
import ReportServices from '@Services/Report.services';
import TaxStatusServices from '@Services/TaxStatus.services';
import { FilterOption, NewReportFields } from '@Types/Form.types';
import {
  DROPDOWN_DEFAULT,
  FIELD_NAMES,
  FILE_NAME_FORMAT,
  REPORT_DATA_RADIO_BUTTON,
  REPORT_FILTER_TYPE,
  REPORT_FILTERS_ASSET,
  REPORT_FILTERS_ASSET_LOG,
  REPORT_TYPE,
} from '@Utils/constants';
import { formatRequired } from '@Utils/form.utils';
import { formatFilterDropdown, formatFilterOptions, formatFilterResults } from '@Utils/format.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { Button, Dropdown, ErrorDisplay, FreeText, Icons, Radio, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styles from './ReportModal.module.scss';

const { REPORTS } = VALIDATION_RULES;
type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
};
const block = 'report-modal';

const ReportModal = ({ show, onClose, onSuccess }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | undefined>(undefined);
  const [filterOptions, setFilterOptions] = useState<{
    [REPORT_TYPE.ASSET]: FilterOption[];
    [REPORT_TYPE.LOG]: FilterOption[];
  }>({
    [REPORT_TYPE.ASSET]: [...REPORT_FILTERS_ASSET],
    [REPORT_TYPE.LOG]: [...REPORT_FILTERS_ASSET_LOG],
  });
  const [filterData, setFilterData] = useState<{
    [key: string]: string[] | boolean | { startDate: Date; endDate: Date } | undefined;
  }>();
  const [currentFilterValue, setCurrentFilterValue] = useState<
    string[] | boolean | { startDate: Date; endDate: Date } | undefined
  >();
  const dropDownoptions = formatFilterDropdown(filterOptions);
  const [enableFilterConfirmButton, setEnableFilterConfirmButton] = useState<boolean>(false);
  const [keywordInput, setKeywordInput] = useState<string>('');

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    control,
    reset,
  } = useForm<NewReportFields>({
    defaultValues: {
      reportType: REPORT_DATA_RADIO_BUTTON[0].VALUE,
    },
  });

  const reportTypeWatch = useWatch({
    control,
    name: FIELD_NAMES.REPORT_TYPE_FIELD,
  });

  useEffect(() => {
    Promise.all([
      AssetStatusServices.getAssetStatus(NaN, false),
      AssetTypeServices.getAssetTypes(NaN, false),
      LocationServices.getLocations(NaN, false),
      TaxStatusServices.getTaxStatus(NaN, false),
    ])
      .then(([assetStatus, assetTypes, location, taxStatus]) => {
        setFilterOptions((options) =>
          formatFilterOptions(
            options,
            assetStatus.data.docs,
            assetTypes.data.docs,
            location.data.docs,
            taxStatus.data.docs,
          ),
        );
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage, true);
      });
    setValue(FIELD_NAMES.REPORT_TYPE_FIELD, REPORT_DATA_RADIO_BUTTON[0].VALUE);
  }, []);

  const resetCurrentFilter = () => {
    setSelectedFilter(undefined);
    setCurrentFilterValue(undefined);
    setEnableFilterConfirmButton(false);
    setKeywordInput('');
  };

  useEffect(() => {
    resetCurrentFilter();
    setFilterData(undefined);
  }, [reportTypeWatch]);

  const handleOnInputChange = (value: string[] | boolean | { startDate: Date; endDate: Date }) => {
    if (
      value === undefined ||
      (Array.isArray(value) && value.length === 0) ||
      (!Array.isArray(value) && typeof value === 'object' && !value.startDate && !value.endDate)
    ) {
      setCurrentFilterValue(undefined);
      setEnableFilterConfirmButton(false);
    } else {
      setCurrentFilterValue(value);
      if (
        selectedFilter?.type !== REPORT_FILTER_TYPE.DATE ||
        ((value as { startDate: Date; endDate: Date })?.startDate &&
          (value as { startDate: Date; endDate: Date })?.endDate)
      ) {
        setEnableFilterConfirmButton(true);
      }
    }
  };
  const isValueValid = () =>
    typeof currentFilterValue !== 'object' ||
    Array.isArray(currentFilterValue) ||
    (currentFilterValue?.startDate && currentFilterValue?.endDate);

  const handleDeleteFilter = (name: string) => {
    setFilterData((prevFilter) => {
      const newFilter = { ...prevFilter };
      delete newFilter[name as keyof typeof prevFilter];
      return Object.keys(newFilter).length === 0 ? undefined : newFilter;
    });
  };

  const handleEditFilter = (name: string) => {
    resetCurrentFilter();
    const newFilter = filterOptions[reportTypeWatch].find((opt) => opt.name === name);
    setSelectedFilter(newFilter);

    if (filterData?.[name]) {
      setCurrentFilterValue(filterData[name]);
    }

    if (filterData?.[name] || newFilter?.type === REPORT_FILTER_TYPE.BOOLEAN_TYPE) {
      setEnableFilterConfirmButton(true);
    }
  };

  const saveNewFilter = () => {
    if (selectedFilter && isValueValid()) {
      if (currentFilterValue !== undefined || keywordInput) {
        setFilterData((oldFilter) => ({
          ...oldFilter,
          [selectedFilter.name]:
            selectedFilter.type === REPORT_FILTER_TYPE.KEYWORD && keywordInput
              ? [...((currentFilterValue ?? []) as string[]), keywordInput]
              : (currentFilterValue ?? []),
        }));
      } else if (selectedFilter.type === REPORT_FILTER_TYPE.BOOLEAN_TYPE) {
        setFilterData((oldFilter) => ({
          ...oldFilter,
          [selectedFilter.name]: false,
        }));
      } else {
        handleDeleteFilter(selectedFilter.name);
      }
      resetCurrentFilter();
    }
  };

  const resetModal = () => {
    reset({
      filename: '',
      timezone: 0,
      reportType: REPORT_DATA_RADIO_BUTTON[0].VALUE,
    });
    resetCurrentFilter();
    setFilterData(undefined);
  };

  const handleSubmitReport = (data: NewReportFields) => {
    data.filename = data.filename.replace(/\s+/g, FILE_NAME_FORMAT);
    data.timezone = new Date().getTimezoneOffset();
    ReportServices.createReport({ ...data, filter: filterData || {} })
      .then(() => {
        onSuccess();
        resetModal();
        Toast.success('Report created successfully.');
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage, true);
      });
  };

  useEffect(() => resetModal(), [show]);

  useEffect(() => {
    if (selectedFilter?.type === REPORT_FILTER_TYPE.KEYWORD) {
      const trimmedKeyword = keywordInput.trim();
      if (trimmedKeyword || (Array.isArray(currentFilterValue) && currentFilterValue.length > 0)) {
        setEnableFilterConfirmButton(true);
      } else {
        setEnableFilterConfirmButton(false);
      }
    }
  }, [keywordInput, currentFilterValue]);

  const getPlaceholder = () => {
    if (selectedFilter?.type === REPORT_FILTER_TYPE.KEYWORD) {
      return REPORT_FILTERS_ASSET.find((filter) => selectedFilter?.name === filter.name)?.placeholder;
    }
    return '';
  };

  return (
    <div
      className={`modal--w-65vw modal--fit-content  modal--pt-${
        Object.keys(filterData || {}).length > 1 ? 15 : 25
      }vh modal--pt-no-mobile`}>
      <ModalWrapper
        title="Create Report"
        show={show}
        handleClose={onClose}
        primaryButtonText="Confirm"
        primaryAction={() => handleSubmit(handleSubmitReport)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <div className='grid'>
            <div className='col-12 col-md-6'>
              <h3>Report Info</h3>
              <FreeText
                label={formatRequired("Filename")}
                fullWidth
                error={!!errors.filename}
                errorMessage={errors.filename?.message}
                {...register('filename', REPORTS.NEW_REPORT.FILE_NAME)}
                placeholder="Filename"
              />
              <h3 className={styles[`${block}__heading`]}>Report Data</h3>
              {REPORT_DATA_RADIO_BUTTON.map((type) => (
                <Radio
                  key={type.LABEL}
                  label={type.LABEL}
                  name={FIELD_NAMES.REPORT_TYPE_FIELD}
                  onChange={() => setValue(FIELD_NAMES.REPORT_TYPE_FIELD, type.VALUE)}
                  isSelected={reportTypeWatch === type.VALUE}
                />
              ))}
              {filterData && (
                <>
                  <h3>Filters</h3>
                  <FilterResult
                    filters={formatFilterResults(filterData, filterOptions[reportTypeWatch])}
                    onDelete={(name) => handleDeleteFilter(name)}
                    onEdit={(name) => handleEditFilter(name)}
                  />
                </>
              )}
            </div>
            <div className='col-12 col-md-6'>
              <h3>Add Filter</h3>
              <div className={styles[`${block}__dropdown-container`]}>
                <Dropdown
                  options={dropDownoptions[reportTypeWatch]}
                  label="Filters"
                  fullWidth
                  onChange={(option) => handleEditFilter(option?.value as string)}
                  value={selectedFilter ? selectedFilter?.name : DROPDOWN_DEFAULT.value}
                />
                <FilterInput
                  filterOption={selectedFilter}
                  onChange={(value) => handleOnInputChange(value)}
                  value={currentFilterValue}
                  keywordInput={keywordInput}
                  onKeywordChange={setKeywordInput}
                  placeholder={getPlaceholder()}
                />

                {selectedFilter && (
                  <div className='content-right'>
                    <Button type='secondary' onClick={() => resetCurrentFilter()} className='button-with-icon'>
                      {Icons.WarningCircle} Cancel
                    </Button>
                    <Button
                      onClick={() => saveNewFilter()}
                      className='button-with-icon'
                      disabled={!enableFilterConfirmButton}>
                      {Icons.Checkmark} Confirm
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalWrapper>
    </div>
  );
};

export default ReportModal;
